import { useContext, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import PropTypes from 'prop-types';
import {
  Announcement,
  Button,
  CheckCircleIcon,
  CloseIcon,
  ProgressBar,
  IconButton,
  NumeratorLogoSparkle,
  ThumbsDownIcon,
  ThumbsUpIcon,
} from '@utilities';

import { createSummaryGPTFeedback } from '@api/instant_survey';

import { SurveyContext } from '../../../../../../utilities/context';

import styles from './_index.module.scss';

const PreviewBanner = ({
  generationError,
  generationErrorMessage,
  onAcceptPreview,
  onCancelPreview,
  onReprompt,
  progressSteps,
  showProgress,
}) => {
  const { jobId, surveyGoals } = useContext(SurveyContext);

  const [isSavingFeedback, setIsSavingFeedback] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const userId = useSelector((state) => state.user?.data?.id);
  const wasHelpful = selectedFeedback === true;
  const wasNotHelpful = selectedFeedback === false;

  useEffect(() => {
    setSelectedFeedback(null);
  }, [jobId]);

  const handleFeedback = async (feedback) => {
    if (isSavingFeedback || feedback === selectedFeedback) return;
    setIsSavingFeedback(true);
    try {
      const feedbackData = {
        feedback: feedback ? 'POSITIVE' : 'NEGATIVE',
        job: jobId,
        user_id: userId,
      };
      await createSummaryGPTFeedback({ data: feedbackData, jobId: jobId });
      setSelectedFeedback(feedback);
    } catch (error) {
      console.error('Failed to save feedback:', error);
    } finally {
      setIsSavingFeedback(false);
    }
  };

  const footer = (
    <div className={styles['preview-banner-footer']}>
      <div className={styles['preview-banner-buttons']}>
        {generationError ? (
          <Button onClick={onReprompt} text="Try Again" variant="ai" />
        ) : (
          <>
            <Button onClick={onAcceptPreview} text="Insert Questions" variant="ai" />
            <Button onClick={onReprompt} text="Edit Prompt" variant="secondary" />
            <Button onClick={onCancelPreview} text="Exit Preview" variant="secondary" />
          </>
        )}
      </div>
      <div className={styles['preview-banner-feedback']}>
        <p>Was this helpful?</p>
        <IconButton
          className={wasHelpful ? styles['active'] : ''}
          onClick={() => handleFeedback(true)}
          tooltip="This was helpful"
        >
          <ThumbsUpIcon />
        </IconButton>
        <IconButton.Danger
          className={wasNotHelpful ? styles['active'] : ''}
          onClick={() => handleFeedback(false)}
          tooltip="This was not helpful"
        >
          <ThumbsDownIcon />
        </IconButton.Danger>
      </div>
    </div>
  );

  const normalizedSteps = useMemo(() => {
    if (!Array.isArray(progressSteps) || progressSteps.length === 0) {
      return [
        { label: 'Analyzing prompt', max: 1, value: 1 },
        { label: 'Thinking', max: 1, value: 1 },
        { label: 'Building questions', max: 1, value: 1 },
        { label: 'Finalizing output', max: 6, value: 3 },
        { label: 'Wrapping up', max: 6, value: 0 },
      ];
    }
    return progressSteps.map((s) => ({
      label: s.label,
      max: typeof s.max === 'number' && s.max > 0 ? s.max : 1,
      value: typeof s.value === 'number' ? s.value : 0,
    }));
  }, [progressSteps]);

  return (
    <div className={styles['preview-banner-wrapper']}>
      <Announcement
        className={styles['preview-banner-insert-announcement']}
        elevation={1}
        footer={footer}
        header={surveyGoals}
        icon={<NumeratorLogoSparkle className={styles['preview-banner-nmr-logo-sparkle']} />}
        text={generationError && generationErrorMessage ? generationErrorMessage : ''}
        variant="info"
      />
      {showProgress && !generationError && (
        <div className={styles['preview-banner-progress']}>
          {normalizedSteps.map(({ label, max, value }, index) => {
            const isComplete = max > 0 && value >= max;
            const disabled = max > 0 && value === 0;
            return (
              <div className={styles['preview-banner-progress-row']} key={`${label}-${index}`}>
                <span className={styles['preview-banner-progress-label']}>
                  {index + 1}. {label}
                </span>
                <div aria-disabled={disabled} className={styles['preview-banner-progress-bar']}>
                  <ProgressBar aria-label={`${label} progress`} max={max} value={value} />
                </div>
                <div className={styles['preview-banner-progress-status']}>
                  {isComplete ? <CheckCircleIcon /> : null}
                </div>
              </div>
            );
          })}
        </div>
      )}
      <IconButton
        aria-label="Exit AI preview"
        className={styles['preview-banner-close']}
        onClick={onCancelPreview}
        tooltip="Exit preview"
      >
        <CloseIcon />
      </IconButton>
    </div>
  );
};

PreviewBanner.propTypes = {
  generationError: PropTypes.bool.isRequired,
  generationErrorMessage: PropTypes.string,
  onAcceptPreview: PropTypes.func.isRequired,
  onCancelPreview: PropTypes.func.isRequired,
  onReprompt: PropTypes.func.isRequired,
  progressSteps: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      max: PropTypes.number,
      value: PropTypes.number,
    })
  ),
  showProgress: PropTypes.bool,
};

export default PreviewBanner;
